// components/LanguageSelector.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../Core/Translations/LanguageContext';
import { Language } from '../../Core/Translations';

interface LanguageSelectorProps {
  theme?: 'light' | 'dark' | 'header';
  dropdownDirection?: 'up' | 'down';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  theme = 'dark',
  dropdownDirection = 'down'
}) => {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Define colors based on theme for better adaptability.
  // 'header' renders the selected-language toggle in white for dark header bars.
  const themeColors = {
    light: {
      text: '#333',
      border: 'rgba(0, 0, 0, 0.2)',
      borderHover: 'rgba(0, 0, 0, 0.4)',
      bgHover: 'rgba(0, 0, 0, 0.05)'
    },
    dark: {
      text: '#000',
      border: 'rgba(255, 255, 255, 0.3)',
      borderHover: 'rgba(255, 255, 255, 0.5)',
      bgHover: 'rgba(255, 255, 255, 0.1)'
    },
    header: {
      text: '#fff',
      border: '#fff',
      borderHover: '#fff',
      bgHover: 'rgba(255, 255, 255, 0.15)'
    }
  };
  const colors = themeColors[theme];

  // The component is scalable—just add new language objects here.
  // Flags are now image URLs instead of emojis for better browser compatibility.
  const languages: { code: Language; name: string; flag: string }[] = [
    { code: 'en', name: t.languages.en, flag: 'https://flagcdn.com/h60/us.png' },
    { code: 'es', name: t.languages.es, flag: 'https://flagcdn.com/h60/es.png' },
    { code: 'fr', name: t.languages.fr, flag: 'https://flagcdn.com/h60/fr.png' },
    { code: 'hi', name: t.languages.hi, flag: 'https://flagcdn.com/h60/in.png' },
    { code: 'bn', name: t.languages.bn, flag: 'https://flagcdn.com/h60/bd.png' },
    { code: 'pt', name: t.languages.pt, flag: 'https://flagcdn.com/h60/pt.png' },
    { code: 'it', name: t.languages.it, flag: 'https://flagcdn.com/h60/it.png' },
    { code: 'ru', name: t.languages.ru, flag: 'https://flagcdn.com/h60/ru.png' },
    { code: 'ja', name: t.languages.ja, flag: 'https://flagcdn.com/h60/jp.png' },
    { code: 'ko', name: t.languages.ko, flag: 'https://flagcdn.com/h60/kr.png' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = languages.find(l => l.code === language);

  // Calculate dropdown position styles based on direction
  const dropdownPositionStyles = dropdownDirection === 'up' 
    ? {
        bottom: '100%',
        marginBottom: '8px',
        animation: 'fadeInUp 0.3s ease'
      }
    : {
        top: '100%',
        marginTop: '8px',
        animation: 'fadeInDown 0.3s ease'
      };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        display: 'inline-block'
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          fontSize: '14px',
          color: colors.text,
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.bgHover;
          e.currentTarget.style.borderColor = colors.borderHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = colors.border;
        }}
      >
        {/* Flag Image for Toggle Button */}
        <img 
          src={currentLang?.flag} 
          alt={`${currentLang?.name} flag`} 
          style={{ width: '24px', height: '18px', objectFit: 'cover', borderRadius: '3px', border: `1px solid ${colors.border}` }} 
        />
        <span>{currentLang?.name}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease'
          }}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

{isOpen && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            ...dropdownPositionStyles,
            background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 100%)',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            minWidth: '180px',
            maxHeight: window.innerWidth <= 970 ? '300px' : 'none',
            overflowY: 'auto',
            zIndex: 1000,
          }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
                transition: 'background 0.2s ease',
                fontWeight: language === lang.code ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(103, 126, 234, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Flag Image for Dropdown Item */}
                <img 
                  src={lang.flag} 
                  alt={`${lang.name} flag`} 
                  style={{ width: '24px', height: '18px', objectFit: 'cover', borderRadius: '3px', border: '1px solid #ddd' }} 
                />
                <span>{lang.name}</span>
              </div>
              {language === lang.code && (
                <span
                  style={{
                    fontSize: '11px',
                    color: '#667eea',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {t.header.currentLanguage}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
       /* Added this media query for mobile screens */
        @media (max-width: 768px) {
          .language-dropdown-menu {
            max-height: 50px; /* Adjust this value as needed */
          }
        }
        /* Custom scrollbar for the dropdown */
        div[style*="overflowY"] {
          scrollbar-width: thin;
          scrollbar-color: rgba(103, 126, 234, 0.3) transparent;
        }

        div[style*="overflowY"]::-webkit-scrollbar {
          width: 6px;
        }

        div[style*="overflowY"]::-webkit-scrollbar-track {
          background: transparent;
        }

        div[style*="overflowY"]::-webkit-scrollbar-thumb {
          background-color: rgba(103, 126, 234, 0.3);
          border-radius: 3px;
        }

        div[style*="overflowY"]::-webkit-scrollbar-thumb:hover {
          background-color: rgba(103, 126, 234, 0.5);
        }
      `}</style>
    </div>
  );
};